import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { NAV } from '@/constants/testIds';
import { Menu, MapPin, Sprout, Bell, Sparkles, MessageSquare } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';

const MSG_POLL_MS = 15000;

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  const loadUnread = useCallback(async () => {
    if (!user) { setUnread(0); return; }
    try {
      const { data } = await api.get('/conversations');
      const total = (data || []).reduce((s, c) => s + (c.unread || 0), 0);
      setUnread(total);
    } catch (_) {/* silent */}
  }, [user]);

  useEffect(() => {
    if (!user) return;
    loadUnread();
    const id = setInterval(loadUnread, MSG_POLL_MS);
    const onVis = () => document.visibilityState === 'visible' && loadUnread();
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, [user, loadUnread]);

  const linkCls = ({ isActive }) =>
    `relative text-sm font-medium transition-colors ${isActive ? 'text-foreground after:absolute after:left-0 after:-bottom-1.5 after:h-[2px] after:w-full after:bg-chartreuse after:rounded-full after:shadow-[0_0_12px_rgba(180,255,0,0.6)]' : 'text-muted-foreground hover:text-foreground'}`;

  return (
    <header className="sticky top-0 z-40 px-3 sm:px-5 pt-3">
      <div className="liquid-glass liquid-glass-sheen noise-overlay max-w-7xl mx-auto rounded-2xl md:rounded-full bg-background/30">
      <div className="relative z-[2] px-4 sm:px-6 lg:px-8 h-14 md:h-16 flex items-center justify-between">
        <Link to="/" data-testid={NAV.logo} className="flex items-center gap-2.5 group">
          <div className="relative">
            <div className="absolute inset-0 rounded-xl bg-chartreuse/30 blur-md opacity-0 group-hover:opacity-100 transition-opacity"/>
            <img src="/logo.png" alt="AgriNexus" className="relative w-9 h-9 rounded-xl object-cover shadow-sm group-hover:scale-105 transition-transform"/>
          </div>
          <span className="font-display text-xl font-medium tracking-tight">AgriNexus</span>
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="/esplora" data-testid={NAV.explore} className={linkCls}>Esplora</NavLink>
          <NavLink to="/partite" data-testid="nav-batches" className={linkCls}>Partite</NavLink>
          <NavLink to="/mappa" data-testid={NAV.map} className={linkCls}>Mappa</NavLink>
          <NavLink to="/pubblica" data-testid={NAV.publish} className={linkCls}>Pubblica terreno</NavLink>
          <NavLink to="/come-funziona" data-testid={NAV.how} className={linkCls}>Come funziona</NavLink>
          {user && <NavLink to="/assistente" className={linkCls}>Assistente AI</NavLink>}
          {user && (
            <NavLink to="/messaggi" data-testid="nav-messages" className={linkCls}>
              <span className="inline-flex items-center gap-1.5">
                <MessageSquare size={14} strokeWidth={1.8}/> Messaggi
                {unread > 0 && (
                  <span data-testid="nav-messages-badge" className="min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-chartreuse text-obsidian text-[10px] font-bold">
                    {unread > 99 ? '99+' : unread}
                  </span>
                )}
              </span>
            </NavLink>
          )}
        </nav>
        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => nav('/dashboard')} data-testid={NAV.dashboard}>Dashboard</Button>
              <Button variant="outline" size="sm" onClick={async () => { await logout(); nav('/'); }} data-testid={NAV.logout}>Esci</Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => nav('/login')} data-testid={NAV.login}>Accedi</Button>
              <Button size="sm" className="rounded-full chartreuse-glow" onClick={() => nav('/registrati')} data-testid={NAV.register}>Registrati</Button>
            </>
          )}
        </div>
        <button className="md:hidden p-2 rounded-full transition-colors hover:bg-white/10" onClick={() => setOpen(!open)} data-testid={NAV.mobileToggle} aria-label="menu">
          <Menu size={20} />
        </button>
      </div>
      {open && (
        <div className="md:hidden relative z-[2] px-5 pb-4 pt-3 space-y-3 border-t border-white/10" data-testid="nav-mobile-menu">
          <NavLink to="/esplora" onClick={()=>setOpen(false)} className="block text-sm">Esplora</NavLink>
          <NavLink to="/partite" onClick={()=>setOpen(false)} data-testid="nav-mobile-batches" className="block text-sm">Partite</NavLink>
          <NavLink to="/mappa" onClick={()=>setOpen(false)} className="block text-sm">Mappa</NavLink>
          <NavLink to="/pubblica" onClick={()=>setOpen(false)} className="block text-sm">Pubblica terreno</NavLink>
          <NavLink to="/come-funziona" onClick={()=>setOpen(false)} className="block text-sm">Come funziona</NavLink>
          {user && (
            <NavLink
              to="/assistente"
              onClick={()=>setOpen(false)}
              data-testid="nav-mobile-assistant"
              className="flex items-center gap-2 text-sm font-medium text-foreground"
            >
              <Sparkles size={14} className="text-chartreuse"/> Assistente AI
            </NavLink>
          )}
          {user && (
            <NavLink
              to="/messaggi"
              onClick={()=>setOpen(false)}
              data-testid="nav-mobile-messages"
              className="flex items-center gap-2 text-sm font-medium text-foreground"
            >
              <MessageSquare size={14}/> Messaggi
              {unread > 0 && (
                <span className="min-w-[18px] h-[18px] px-1 ml-auto grid place-items-center rounded-full bg-chartreuse text-obsidian text-[10px] font-bold">
                  {unread > 99 ? '99+' : unread}
                </span>
              )}
            </NavLink>
          )}
          {user ? (
            <>
              <button className="block text-sm" onClick={() => { setOpen(false); nav('/dashboard'); }}>Dashboard</button>
              <button className="block text-sm" onClick={async () => { setOpen(false); await logout(); nav('/'); }}>Esci</button>
            </>
          ) : (
            <>
              <button className="block text-sm" onClick={() => { setOpen(false); nav('/login'); }}>Accedi</button>
              <button className="block text-sm" onClick={() => { setOpen(false); nav('/registrati'); }}>Registrati</button>
            </>
          )}
        </div>
      )}
      </div>
    </header>
  );
}
